//
// Copyright 2024 DXOS.org
//

import * as Atom from 'effect/reactivity/Atom';
import type * as Registry from 'effect/reactivity/AtomRegistry';

export type Attention = {
  hasAttention: boolean;
  isAncestor: boolean;
  isRelated: boolean;
};

/**
 * Manages attention state for an application.
 * Attention keys are slash-qualified graph IDs; ancestry is derived from progressive prefixes.
 */
export class AttentionManager {
  readonly #map = new Map<string, Atom.Writable<Attention>>();
  readonly #currentAtom: Atom.Writable<string[]>;
  readonly #registry: Registry.AtomRegistry;

  constructor(registry: Registry.AtomRegistry, initial: string[] = []) {
    this.#registry = registry;
    this.#currentAtom = Atom.make<string[]>([]).pipe(Atom.keepAlive);
    if (initial.length > 0) {
      this.update(initial);
    }
  }

  /**
   * Atom for the currently attended element IDs.
   */
  get current(): Atom.Atom<string[]> {
    return this.#currentAtom;
  }

  /**
   * Gets the currently attended element IDs.
   */
  getCurrent(): readonly string[] {
    return this.#registry.get(this.#currentAtom);
  }

  /**
   * Subscribe to changes in the current attention IDs.
   */
  subscribeCurrent(cb: (current: readonly string[]) => void): () => void {
    this.#registry.get(this.#currentAtom);
    return this.#registry.subscribe(this.#currentAtom, () => {
      cb(this.#registry.get(this.#currentAtom));
    });
  }

  /**
   * All tracked qualified IDs.
   */
  keys(): string[] {
    return Array.from(this.#map.keys());
  }

  /**
   * Get the attention state for a given qualified ID.
   */
  get(id: string): Attention {
    const atom = this.#getAtom(id);
    return this.#registry.get(atom);
  }

  /**
   * Subscribe to changes in the attention state for a given qualified ID.
   */
  subscribe(id: string, cb: (attention: Attention) => void): () => void {
    const atom = this.#getAtom(id);
    this.#registry.get(atom);
    return this.#registry.subscribe(atom, () => {
      cb(this.#registry.get(atom));
    });
  }

  #getAtom(id: string): Atom.Writable<Attention> {
    const existing = this.#map.get(id);
    if (existing) {
      return existing;
    }

    const newAtom = Atom.make<Attention>({ hasAttention: false, isAncestor: false, isRelated: false }).pipe(
      Atom.keepAlive,
    );
    this.#map.set(id, newAtom);
    return newAtom;
  }

  /**
   * Update the currently attended element.
   * Takes the array of qualified IDs collected from the DOM; the first element is the primary attended item.
   * Ancestry is derived from the progressive prefixes of the primary ID.
   * Relatedness is derived from the segment ID: any tracked key whose last `/` segment matches the
   * attended ID's segment ID is marked `isRelated`. Additionally, if the primary ID is a linked segment
   * (starts with `~`), its immediate parent gets `isRelated` alongside `isAncestor`.
   *
   * @internal
   */
  update(nextIds: string[]): void {
    const primaryId = nextIds[0];
    if (!primaryId) {
      return;
    }

    const currentIds = this.getCurrent();
    const prevPrimaryId = currentIds[0];
    // Clear previous attention state: ancestors, primary, and related keys.
    if (prevPrimaryId) {
      const prevPrefixes = expandAttendableId(prevPrimaryId);
      for (const prefix of prevPrefixes) {
        this.#set(prefix, {});
      }
      const prevSegmentId = getSegmentId(prevPrimaryId);
      for (const key of this.keys()) {
        if (getSegmentId(key) === prevSegmentId) {
          this.#set(key, {});
        }
      }
    }

    this.#registry.set(this.#currentAtom, nextIds);

    // Set ancestors and primary.
    const prefixes = expandAttendableId(primaryId);
    const prefixSet = new Set(prefixes);
    const linkedParent = isLinkedSegment(primaryId) ? getParentId(primaryId) : undefined;

    for (const prefix of prefixes) {
      if (prefix === primaryId) {
        this.#set(prefix, { hasAttention: true });
      } else if (prefix === linkedParent) {
        this.#set(prefix, { isAncestor: true, isRelated: true });
      } else {
        this.#set(prefix, { isAncestor: true });
      }
    }

    // Set related keys: any tracked key sharing the same segment ID.
    const segmentId = getSegmentId(primaryId);
    for (const key of this.keys()) {
      if (!prefixSet.has(key) && getSegmentId(key) === segmentId) {
        this.#set(key, { isRelated: true });
      }
    }
  }

  #set(id: string, attention: Partial<Attention>): void {
    const atom = this.#getAtom(id);
    this.#registry.set(atom, {
      hasAttention: attention.hasAttention ?? false,
      isAncestor: attention.isAncestor ?? false,
      isRelated: attention.isRelated ?? false,
    });
  }
}

/** The attribute marking an element as attendable, carrying its qualified id. */
export const ATTENDABLE_ATTRIBUTE = 'data-attendable-id';

/** Selector matching any attendable element. */
export const ATTENDABLE_SELECTOR = `[${ATTENDABLE_ATTRIBUTE}]`;

/**
 * Accumulates all attendable IDs between the element provided and the root, inclusive.
 * Each `data-attendable-id` value is treated as a single qualified ID (no splitting).
 */
export const getAttendables = (selector: string, cursor: Element, acc: string[] = []): string[] => {
  // Find the closest element with `data-attendable-id`, if any; start from cursor and move up the DOM tree.
  const closestAttendable = cursor.closest(selector);
  if (closestAttendable) {
    const attendableId = closestAttendable.getAttribute(ATTENDABLE_ATTRIBUTE);
    if (!attendableId) {
      // This has an id of an aria-controls elsewhere on the page, move cursor to that trigger.
      const trigger = document.querySelector(`[aria-controls="${closestAttendable.getAttribute('id')}"]`);
      if (!trigger) {
        return acc;
      } else {
        return getAttendables(selector, trigger, acc);
      }
    } else {
      acc.push(attendableId);
      return !closestAttendable.parentElement ? acc : getAttendables(selector, closestAttendable.parentElement, acc);
    }
  }

  return [...new Set(acc)];
};

/**
 * Attend `element` the way focusing it would, without moving focus. Answers the attended ids, or
 * undefined when nothing changed: an element outside any attendable leaves attention where it was.
 */
export const attendElement = (attention: AttentionManager, element: Element): string[] | undefined => {
  const selector = [
    ATTENDABLE_SELECTOR,
    ...Array.from(document.querySelectorAll('[aria-controls]')).map(
      (el) => `[id="${el.getAttribute('aria-controls')}"]`,
    ),
  ].join(',');
  const prev = attention.getCurrent();
  const next = getAttendables(selector, element);
  if (next.length === 0 || (prev.length === next.length && prev.every((id, index) => next[index] === id))) {
    return undefined;
  }

  attention.update(next);
  return next;
};

/**
 * The outermost attendable ancestor of `element` — the root of any nested attendables (e.g. a section
 * within a stack). Structural (real DOM ancestry), so it is independent of what currently has attention.
 * Resolve it from an in-DOM element: a portaled subtree (a menu, a popover) is not under its own
 * attendable, so walk from the trigger or anchor instead.
 */
export const getRootAttendableId = (element: Element): string | undefined =>
  getAttendables(ATTENDABLE_SELECTOR, element).at(-1);

export type AttendableId = { attendableId?: string };

export type Related = { related?: boolean };

/**
 * Prefix for linked segments. A linked segment's attention state is shared with its parent:
 * when a linked node has attention, the parent is marked as both ancestor and related.
 */
const LINKED_PREFIX = '~';

/**
 * Decompose a qualified graph ID into progressive prefixes for ancestry tracking.
 * e.g. `root/a/b/c` yields `['root', 'root/a', 'root/a/b', 'root/a/b/c']`.
 */
export const expandAttendableId = (qualifiedId: string): string[] => {
  const segments = qualifiedId.split('/');
  return segments.map((_, index) => segments.slice(0, index + 1).join('/'));
};

/**
 * Whether the last segment of a qualified ID is a linked segment (starts with `~`).
 * Linked segments share their attention state with the parent node.
 */
export const isLinkedSegment = (qualifiedId: string): boolean => {
  const lastSegment = qualifiedId.split('/').pop() ?? '';
  return lastSegment.startsWith(LINKED_PREFIX);
};

/**
 * Build a linked segment ID from a variant name (e.g., `'settings'` -> `'~settings'`).
 * Nodes identified by linked segments share their attention state with the parent node.
 */
export const linkedSegment = (variant: string): string => `${LINKED_PREFIX}${variant}`;

/**
 * Extract the variant name from the last segment of a qualified ID, stripping the linked prefix.
 * e.g. `'root/space/obj/~settings'` -> `'settings'`.
 * If the segment is not linked (no `~` prefix), returns the segment as-is.
 */
export const getLinkedVariant = (qualifiedId: string): string => {
  const lastSegment = qualifiedId.split('/').pop() ?? '';
  return lastSegment.startsWith(LINKED_PREFIX) ? lastSegment.slice(LINKED_PREFIX.length) : lastSegment;
};

/**
 * Get the parent qualified ID (everything before the last `/` segment).
 */
export const getParentId = (qualifiedId: string | undefined): string | undefined => {
  if (!qualifiedId) {
    return undefined;
  }

  const lastSlash = qualifiedId.lastIndexOf('/');
  return lastSlash > 0 ? qualifiedId.slice(0, lastSlash) : undefined;
};

/**
 * Extract the segment ID (last segment) of a qualified ID.
 */
export const getSegmentId = (qualifiedId: string): string => {
  return qualifiedId.split('/').pop() ?? qualifiedId;
};
